import { ChevronRight, Play } from 'lucide-react'
import clsx from 'clsx'
import { detailGhostBtn, reportCardSub, reportCardTitle, reportEyebrow } from '../detailUi'
import type { DetailSection } from './types'
import { DetailFields } from './DetailFields'
const ACCENT = {
  danger: 'border-rose-200/80 bg-rose-50/50 ring-rose-900/5 hover:bg-rose-50',
  warn: 'border-amber-200/80 bg-amber-50/60 ring-amber-900/5 hover:bg-amber-50',
  neutral: 'border-slate-200/80 bg-white ring-gray-900/5 hover:bg-slate-50',
} as const

export function DetailSectionCard({ section, onOpen, onReplay }: {
  section: DetailSection
  onOpen: (id: DetailSection['id']) => void
  onReplay?: () => void
}): JSX.Element {
  const accent = section.accent ?? 'neutral'
  const faceFields = section.expandOnCard ? section.fields : section.highlights
  const showReplay = section.replayable && onReplay
  return (
    <div
      className={clsx(
        'group flex w-full flex-col rounded-xl border p-4 text-left shadow-sm ring-1 transition-colors',
        section.wide && 'sm:col-span-2',
        ACCENT[accent],
      )}
    >
      <button
        type="button"
        onClick={() => onOpen(section.id)}
        className="flex w-full flex-col text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className={clsx(reportCardTitle, 'truncate')}>{section.title}</h3>
            {section.subtitle && (
              <p className={clsx(reportCardSub, 'mt-0.5 truncate')}>{section.subtitle}</p>
            )}
          </div>
          <ChevronRight
            size={16}
            className="mt-0.5 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-blue-600"
          />
        </div>

        {section.body ? (
          <p className="line-clamp-3 text-sm leading-snug text-slate-600">{section.body}</p>
        ) : (
          <DetailFields fields={faceFields} />
        )}

        <span className={clsx(reportEyebrow, 'mt-3 group-hover:text-blue-600')}>
          View details
        </span>
      </button>

      {showReplay && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onReplay() }}
          className={clsx(detailGhostBtn, 'mt-3 self-start')}
        >
          <Play size={12} />
          Replay
        </button>
      )}
    </div>
  )
}
